import { PreviewTask, TasksSource } from "@jugl-web/rest-api/tasks";
import { TaskCustomFieldType } from "@jugl-web/rest-api/tasks/models/TaskCustomField";
import { isoToLocalDate, useTranslations } from "@jugl-web/utils";
import { useFormattedDate } from "@jugl-web/utils/hooks/useFormattedDate";
import { useLanguage } from "@jugl-web/utils/i18n/EnhancedIntlProvider";
import format from "date-fns/format";
import { useCallback, useMemo } from "react";
import { extractBoardIdFromTasksSource, getTasksSourceInfo } from "../../utils";
import { useTaskBoardsQuery } from "../useTaskBoardsQuery";
import { useTaskCustomFields } from "../useTaskCustomFields";
import { useTaskFieldList } from "../useTaskFieldList";
import { useTaskLabels } from "../useTaskLabels";
import { useTaskObjectNaming } from "../useTaskObjectNaming";
import { useTaskPriorities } from "../useTaskPriorities";
import { useTaskStatusesGetters } from "../useTaskStatusesGetters";
import { ReactComponent as AllIcon } from "./assets/all.svg";
import { ReactComponent as BoardIcon } from "./assets/board.svg";
import { ReactComponent as ClientIcon } from "./assets/client.svg";
import { ReactComponent as DateIcon } from "./assets/date.svg";
import { ReactComponent as DropdownIcon } from "./assets/dropdown.svg";
import { ReactComponent as LabelIcon } from "./assets/label.svg";
import { ReactComponent as NameIcon } from "./assets/name.svg";
import { ReactComponent as NumberIcon } from "./assets/number.svg";
import { ReactComponent as OrderIcon } from "./assets/order.svg";
import { ReactComponent as PriorityIcon } from "./assets/priority.svg";
import { ReactComponent as StatusIcon } from "./assets/status.svg";
import { ReactComponent as TextIcon } from "./assets/text.svg";

const customFieldTypeToIcon: Record<TaskCustomFieldType, React.ReactNode> = {
  text: <TextIcon />,
  date: <DateIcon />,
  dropdown: <DropdownIcon />,
  number: <NumberIcon />,
};

export type SearchQueryFilter = {
  id: string;
  title: string;
  backgroundColor: string;
  icon: React.ReactNode;
  isHidden?: boolean;
  isDefault?: boolean;
};

export const useTaskSearchQueryFilters = ({
  entityId,
  source,
}: {
  entityId: string;
  source: TasksSource;
}) => {
  const sourceInfo = getTasksSourceInfo(source);
  const boardId = extractBoardIdFromTasksSource(source) || null;

  const { boardCustomFields, getCustomFieldById } = useTaskCustomFields({
    entityId,
    boardId,
  });

  const { isFieldEnabled } = useTaskFieldList({ entityId, boardId });

  const { getStatusByBoard } = useTaskStatusesGetters({ entityId });

  const { getLabelById, areLabelsSupported } = useTaskLabels({ entityId });
  const { getBoardByIdFromCache } = useTaskBoardsQuery({ entityId });
  const { getPriorityDetailsById } = useTaskPriorities();

  const taskObjectNaming = useTaskObjectNaming({
    entityId,
    boardId,
  });

  const { t } = useTranslations();
  const { formatDateTimeLabel } = useFormattedDate();
  const { dateLocale } = useLanguage();

  const filterTasksByName = useCallback(
    (task: PreviewTask, searchQuery: string) =>
      task.name.toLowerCase().includes(searchQuery.toLowerCase()),
    []
  );

  const filterTasksByOrderId = useCallback(
    (task: PreviewTask, searchQuery: string) =>
      task.order_id?.toLowerCase().includes(searchQuery.toLowerCase()) || false,
    []
  );

  const filterTasksByStatus = useCallback(
    (task: PreviewTask, searchQuery: string) => {
      const status = getStatusByBoard(task.board_id, task.status);
      if (!status) return false;
      return status.text.toLowerCase().includes(searchQuery.toLowerCase());
    },
    [getStatusByBoard]
  );

  const filterTasksByBoard = useCallback(
    (task: PreviewTask, searchQuery: string) => {
      if (!task.board_id) return false;
      const board = getBoardByIdFromCache(task.board_id);
      if (!board) return false;
      return board.name.toLowerCase().includes(searchQuery.toLowerCase());
    },
    [getBoardByIdFromCache]
  );

  const filterTasksByLabel = useCallback(
    (task: PreviewTask, searchQuery: string) => {
      if (!task.label_id) return false;
      const label = getLabelById(task.label_id);
      if (!label) return false;
      return label.text.toLowerCase().includes(searchQuery.toLowerCase());
    },
    [getLabelById]
  );

  const filterTasksByPriority = useCallback(
    (task: PreviewTask, searchQuery: string) => {
      const priority = getPriorityDetailsById(task.priority);
      return priority.longLabel
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    },
    [getPriorityDetailsById]
  );

  const filterTasksByDate = useCallback(
    (task: PreviewTask, searchQuery: string) => {
      if (!task.due_at) return false;
      return formatDateTimeLabel(isoToLocalDate(task.due_at))
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    },
    [formatDateTimeLabel]
  );

  const filterTasksByClient = useCallback(
    (task: PreviewTask, searchQuery: string) => {
      if (!task.customer) return false;
      const lowerSearchQuery = searchQuery.toLowerCase();

      return [
        {
          id: "name",
          searchMatch: false,
        },
        {
          id: "email",
          searchMatch: false,
        },
        {
          id: "phone",
          searchMatch: false,
        },
      ]
        .map((match) => {
          if (!task.customer) return match;

          if (match.id === "name") {
            match.searchMatch = task.customer.first_name
              .toLowerCase()
              .includes(lowerSearchQuery);
          }

          if (match.id === "email") {
            match.searchMatch =
              task.customer.email?.toLowerCase().includes(lowerSearchQuery) ??
              false;
          }

          if (match.id === "phone" && task.customer.phone_number) {
            match.searchMatch =
              (task.customer.phone_number.startsWith("+")
                ? task.customer.phone_number
                : `+${task.customer.phone_number}`
              )
                ?.toLowerCase()
                .includes(lowerSearchQuery) ?? false;
          }

          return match;
        })
        .some((match) => match.searchMatch);
    },
    []
  );

  const filterTasksByCustomField = useCallback(
    (customFieldId: string) => (task: PreviewTask, searchQuery: string) => {
      const customField = getCustomFieldById(customFieldId);
      if (
        !task.custom_fields ||
        !customField ||
        !Object.keys(task.custom_fields).find(
          (fieldId) => fieldId === customFieldId
        )
      ) {
        return false;
      }

      let value: string | undefined = task.custom_fields[customFieldId];

      if (!value) return false;

      if (customField.type === "date") {
        value = format(new Date(value), "d MMMM, yyyy, hh:mm a", {
          locale: dateLocale,
        });
      } else if (customField.type === "dropdown") {
        value = customField.values?.find((v) => v.id === value)?.value;
        if (!value) return false;
      }
      return value.toLowerCase().includes(searchQuery.toLowerCase());
    },
    [getCustomFieldById, dateLocale]
  );

  const taskSearchQueryFilterIdToFilterFunction: Record<
    string,
    (task: PreviewTask, searchQuery: string) => boolean
  > = useMemo(
    () => ({
      name: filterTasksByName,
      order: filterTasksByOrderId,
      board: filterTasksByBoard,
      label: filterTasksByLabel,
      status: filterTasksByStatus,
      priority: filterTasksByPriority,
      date: filterTasksByDate,
      client: filterTasksByClient,
      ...Object.fromEntries(
        boardCustomFields.map((customField) => [
          customField.id,
          (task: PreviewTask, searchQuery: string) =>
            filterTasksByCustomField(customField.id)(task, searchQuery),
        ])
      ),
    }),
    [
      filterTasksByName,
      filterTasksByOrderId,
      filterTasksByBoard,
      filterTasksByLabel,
      filterTasksByStatus,
      filterTasksByPriority,
      filterTasksByDate,
      filterTasksByClient,
      boardCustomFields,
      filterTasksByCustomField,
    ]
  );

  const getTaskSearchQueryFilters = useCallback(
    (
      selectedTaskSearchQueryFilters: string[]
    ): ((task: PreviewTask, searchQuery: string) => boolean)[] => [
      taskSearchQueryFilterIdToFilterFunction.name,
      taskSearchQueryFilterIdToFilterFunction.order,
      ...selectedTaskSearchQueryFilters
        .filter((id) => id !== "order")
        .map((id) => taskSearchQueryFilterIdToFilterFunction[id])
        .filter(Boolean),
    ],
    [taskSearchQueryFilterIdToFilterFunction]
  );

  const taskSearchQueryFilters = useMemo(
    (): SearchQueryFilter[] =>
      [
        {
          id: "all",
          title: t({
            id: "tasks-page.all-columns",
            defaultMessage: "All columns",
          }),
          backgroundColor: "bg-[#6192BE]",
          icon: <AllIcon />,
        },
        {
          id: "name",
          title: t(
            {
              id: "tasks-page.task-name",
              defaultMessage: "{singularTaskObjectName} Name",
            },
            {
              singularTaskObjectName: taskObjectNaming.singular,
            }
          ),
          backgroundColor: "bg-primary",
          icon: <NameIcon />,
          isDefault: true,
        },
        {
          id: "date",
          title: t({
            id: "tasks-page.due-date",
            defaultMessage: "Due date",
          }),
          backgroundColor: "bg-[#22C59E]",
          icon: <DateIcon />,
          isHidden: !isFieldEnabled("due_date"),
        },
        {
          id: "order",
          title: t(
            {
              id: "tasks-page.task-id",
              defaultMessage: "{singularTaskObjectName} ID",
            },
            { singularTaskObjectName: taskObjectNaming.singular }
          ),
          backgroundColor: "bg-[#B021F3]",
          icon: <OrderIcon />,
        },
        {
          id: "board",
          title: t({
            id: "tasks-page.board",
            defaultMessage: "Board",
          }),
          backgroundColor: "bg-primary",
          icon: <BoardIcon />,
          isHidden: !isFieldEnabled("board") || sourceInfo.isUserBoardTasks,
        },
        {
          id: "label",
          title: t({
            id: "tasks-page.label",
            defaultMessage: "Label",
          }),
          backgroundColor: "bg-[#22C59E]",
          icon: <LabelIcon />,
          isHidden: !isFieldEnabled("label") || !areLabelsSupported,
        },
        {
          id: "status",
          title: t({
            id: "tasks-page.status",
            defaultMessage: "Status",
          }),
          backgroundColor: "bg-[#B021F3]",
          icon: <StatusIcon />,
          isHidden: !isFieldEnabled("status"),
        },
        {
          id: "priority",
          title: t({
            id: "tasks-page.priority",
            defaultMessage: "Priority",
          }),
          backgroundColor: "bg-[#F33A9E]",
          icon: <PriorityIcon />,
          isHidden: !isFieldEnabled("priority"),
        },
        {
          id: "client",
          title: t({
            id: "tasks-page.customer",
            defaultMessage: "Customer",
          }),
          backgroundColor: "bg-primary",
          icon: <ClientIcon />,
          isHidden: !isFieldEnabled("customer") || sourceInfo.isCustomerTasks,
        },
        ...boardCustomFields.map((customField) => ({
          id: customField.id,
          title: customField.name,
          backgroundColor: "bg-[#F15C5C]",
          icon: customFieldTypeToIcon[customField.type],
          isHidden: !isFieldEnabled(customField.id),
        })),
      ].filter((filter) => !filter.isHidden),
    [
      t,
      taskObjectNaming.singular,
      isFieldEnabled,
      sourceInfo.isUserBoardTasks,
      sourceInfo.isCustomerTasks,
      areLabelsSupported,
      boardCustomFields,
    ]
  );

  return {
    taskSearchQueryFilters,
    getTaskSearchQueryFilters,
  };
};
